// Binds every id in routeManifest.ts to a component (and the ProtectedRoute
// guard). Split out from App.tsx so it can be exercised directly by
// routeBindings.test.tsx without mounting the whole provider stack --
// renderRouteManifest throws on a missing binding or guard, so that test
// asserts it does not throw for any manifest node.
//
// Every feature page below is bound here ONCE, in phase 0 (execution plan
// §8: "the mechanism that makes routeBindings.tsx freezable"). Deferred
// routes use named unavailable states so their route identity and recovery
// action stay truthful without implying that the future capability shipped.
import type { ComponentType } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ProtectedRoute } from '../../auth/ProtectedRoute';
import { useLocalPasswordSession } from '../../auth/localPasswordSession';
import { LoginPage } from '../../auth/LoginPage';
import { RootRedirect } from './RootRedirect';
import { RouteRecoveryScreen } from '../states/RouteStates';
import { AppShell } from '../shell/AppShell';
import { AccountsPage } from '../../features/accounts/AccountsPage';
import { AccountDetailPage } from '../../features/accounts/AccountDetailPage';
import { ConnectPage } from '../../features/connect/ConnectPage';
import { TrendPage } from '../../features/networth/TrendPage';
import { DayPage } from '../../features/networth/DayPage';
import { TransactionsPage } from '../../features/transactions/TransactionsPage';
import { TransactionDetailPage } from '../../features/transactions/TransactionDetailPage';
import { CategoriesPage } from '../../features/transactions/CategoriesPage';
import { MerchantsPage } from '../../features/transactions/MerchantsPage';
import { InboxPage } from '../../features/inbox/InboxPage';
import { SettingsPage } from '../../features/settings/SettingsPage';
import { ReceiptForwardingInstructionsPage } from '../../features/settings/ReceiptForwardingInstructionsPage';
import { BusinessPage } from '../../features/business/BusinessPage';
import { ConditionsPage } from '../../features/business/ConditionsPage';
import { ExportPage } from '../../features/business/ExportPage';
import { EventPage } from '../../features/business/EventPage';
import { AccountantRoute } from '../../features/accountant-shell/AccountantRoute';
import InvitationAcceptPage from '../../features/accountant-access/InvitationAcceptPage';
import { defineRouteBinding } from './renderRouteManifest';

function withShell<P extends object>(Component: ComponentType<P>) {
  return function ShellWrapped(props: P) {
    return (
      <AppShell>
        <Component {...props} />
      </AppShell>
    );
  };
}

function AccountantInvitationAcceptRoute() {
  const { isAuthenticated } = useAuth0();
  const localSession = useLocalPasswordSession();
  const page = <InvitationAcceptPage />;
  // The invitation preview is public, but a signed-in accept must retain the
  // authenticated API session after leaving /accounts. This conditional
  // keeps signed-out previews public and reuses the canonical session guard
  // once either supported auth path is present.
  if (isAuthenticated || localSession?.token) return <ProtectedRoute>{page}</ProtectedRoute>;
  return page;
}

export const routeBindings = {
  login: defineRouteBinding(LoginPage),
  'callback-failure': defineRouteBinding(RouteRecoveryScreen, () => ({ kind: 'session-check-failure' })),
  unavailable: defineRouteBinding(RouteRecoveryScreen, () => ({ kind: 'api-unavailable' })),
  offline: defineRouteBinding(RouteRecoveryScreen, () => ({ kind: 'offline' })),
  'root-redirect': defineRouteBinding(withShell(RootRedirect)),
  connect: defineRouteBinding(withShell(ConnectPage)),
  accounts: defineRouteBinding(withShell(AccountsPage)),
  'accounts-day': defineRouteBinding(withShell(DayPage)),
  'accounts-trend': defineRouteBinding(withShell(TrendPage)),
  'account-detail': defineRouteBinding(withShell(AccountDetailPage)),
  activity: defineRouteBinding(withShell(TransactionsPage)),
  'spend-by-merchant': defineRouteBinding(withShell(MerchantsPage)),
  'activity-item': defineRouteBinding(withShell(TransactionDetailPage)),
  inbox: defineRouteBinding(withShell(InboxPage)),
  'spend-by-category': defineRouteBinding(withShell(CategoriesPage)),
  'business-year': defineRouteBinding(withShell(BusinessPage)),
  rules: defineRouteBinding(withShell(ConditionsPage)),
  'export-review': defineRouteBinding(withShell(ExportPage)),
  event: defineRouteBinding(withShell(EventPage)),
  settings: defineRouteBinding(withShell(SettingsPage)),
  'settings-receipt-email': defineRouteBinding(withShell(ReceiptForwardingInstructionsPage)),
  'accountant-invitation': defineRouteBinding(AccountantInvitationAcceptRoute),
  'accountant-review': defineRouteBinding(AccountantRoute),
  'accountant-open-items': defineRouteBinding(AccountantRoute),
  'accountant-schedule-c': defineRouteBinding(AccountantRoute),
  'accountant-exports': defineRouteBinding(AccountantRoute),
  'not-found': defineRouteBinding(RouteRecoveryScreen, () => ({ kind: 'not-found' })),
};

export const routeGuards = { ProtectedRoute };
